import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { FiChevronLeft, FiChevronRight, FiInfo } from "react-icons/fi";
import { BiCommentDetail } from "react-icons/bi";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Layout from "components/Layout";
import Loading from "components/Loading";
import DataTable from "components/DataTable";
import Modal from "components/Modal";
import TimesNavigationTabs from "components/Times/TimesNavigationTabs";
import EmployeeSelectSearch from "components/EmployeeSelectSearch/EmployeeSelectSearch";
import ManualEntryModal from "components/TimesBalance/ManualEntryModal";
import ManualEntryCreateCell from "components/TimesBalance/ManualEntryCreateCell";
import { timesService, transactionsService, accountsService } from "services";
import { showModal, hideModal } from "actions/modalActions";
import { isAdmin, isSupervisor } from "utils/utils";
import { isHaddockEnvironment } from "utils/documentUtils";
import {
	ACCUMULATED_VIEW,
	balanceSpanClass,
	buildWeeklyBalanceStats,
	clockedHoursAsReglaTransactions,
	defaultAccumulatedView,
	findFirstDayWithHours,
	groupManualEntriesByDay,
	minutesToDisplay,
	minutesToSignedHHmm,
	parseAccumulatedView,
	pickCumulativeAfterManual,
	transactionValueToMinutes,
} from "utils/timeBalance";
import "./TimesBalance.scss";

const NUM_WEEKS = 5;

const CLOCKED_HOURS_ACCOUNT_QUERY = "Horas de trabajo fichadas";
const OBJECTIVE_ACCOUNT_QUERY = "Horas objetivo";
// Regla de sistema «Horas de trabajo fichadas». Su valor se calcula al vuelo y ya no deja transacciones.
const CLOCKED_HOURS_RULE_SYSTEM_ID = 6;

function assignmentCounterHours(response) {
	if (response?.ok === false) return 0;
	const value = response?.data?.[0]?.counters?.[0]?.value;
	const hours = typeof value === "number" ? value : parseFloat(value);
	return Number.isFinite(hours) ? hours : 0;
}

function fetchClockedHours(employeeId, start, end) {
	return accountsService
		.getAccountAssignments({
			employee_id: employeeId,
			start,
			end,
			transaction_rule_system_id: CLOCKED_HOURS_RULE_SYSTEM_ID,
			page: 0,
			page_size: 1,
		})
		.then(assignmentCounterHours);
}

function getWeeksForWindow(windowStart) {
	return Array.from({ length: NUM_WEEKS }, (_, i) => {
		const start = moment(windowStart).add(i, "weeks").startOf("isoWeek");
		return {
			start: start.clone(),
			end: start.clone().endOf("isoWeek"),
			weekNumber: start.isoWeek(),
			year: start.isoWeekYear(),
		};
	});
}

const WeekHeader = ({ week }) => {
	const { t } = useTranslation();
	return (
		<div className="tb-week-header">
			<div className="tb-week-label">
				{t("times.balance.week", { weekNumber: week.weekNumber })}
			</div>
			<div className="tb-week-range">
				{week.start.format("D MMM").toUpperCase()} -{" "}
				{week.end.format("D MMM").toUpperCase()}
			</div>
		</div>
	);
};

const ROW_IDS = {
	trabajadas: "trabajadas",
	objetivo: "objetivo",
	balance: "balance",
	acumuladoContract: "acumuladoContract",
	acumuladoFirstClockIn: "acumuladoFirstClockIn",
	manual: "manual",
	createManual: "createManual",
	balanceAfterManual: "balanceAfterManual",
	acumuladoAfterManual: "acumuladoAfterManual",
};

const BALANCE_ROW_IDS = new Set([
	ROW_IDS.balance,
	ROW_IDS.acumuladoContract,
	ROW_IDS.acumuladoFirstClockIn,
	ROW_IDS.balanceAfterManual,
	ROW_IDS.acumuladoAfterManual,
]);

function mapManualEntries(transactions) {
	return transactions.map((tx) => ({
		id: tx.id,
		day: tx.day,
		value: tx.value,
		minutes: transactionValueToMinutes(tx.value),
		observations: tx.observations,
	}));
}

function renderManualEntryPill(entry, weekIndex, openManualEntryModal, t) {
	const hasObservations = Boolean(entry.observations?.trim());
	const pillButton = (
		<button
			type="button"
			className="tb-manual-pill"
			onClick={() =>
				openManualEntryModal(weekIndex, {
					id: entry.id,
					day: entry.day,
					value: entry.value,
					observations: entry.observations,
				})
			}
			title={
				hasObservations ? undefined : t("times.balance.manualEntry.editTitle")
			}
		>
			{minutesToSignedHHmm(entry.minutes)}
			{hasObservations && (
				<BiCommentDetail className="tb-manual-pill__comment-icon" aria-hidden="true" />
			)}
		</button>
	);

	if (!hasObservations) {
		return <React.Fragment key={entry.id}>{pillButton}</React.Fragment>;
	}

	return (
		<OverlayTrigger
			key={entry.id}
			placement="top"
			overlay={
				<Tooltip className="tooltip-balance">{entry.observations}</Tooltip>
			}
		>
			{pillButton}
		</OverlayTrigger>
	);
}

const TimesBalance = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const history = useHistory();
	const user = useSelector((state) => state.authReducer.user);
	const canSupervise = isAdmin() || isSupervisor();

	const defaultWindowStart = useMemo(
		() => moment().startOf("isoWeek").subtract(NUM_WEEKS - 1, "weeks"),
		[]
	);

	const [windowStart, setWindowStart] = useState(defaultWindowStart);
	const [employeeId, setEmployeeId] = useState(String(user.id));
	const [knownEmployee, setKnownEmployee] = useState({
		id: user.id,
		name: user.name,
		surname: user.surname,
	});
	const [weekData, setWeekData] = useState([]);
	const [loading, setLoading] = useState(true);
	const objetivoAccountIdRef = useRef(null);
	const fichadasAccountIdRef = useRef(null);
	const [editingCreateWeekIndex, setEditingCreateWeekIndex] = useState(null);
	const [accumulatedView, setAccumulatedView] = useState(
		ACCUMULATED_VIEW.CONTRACT
	);
	const userPickedAccumulatedViewRef = useRef(false);

	const handleAccumulatedViewChange = useCallback((event) => {
		userPickedAccumulatedViewRef.current = true;
		setAccumulatedView(parseAccumulatedView(event.target.value));
	}, []);

	const weeks = useMemo(() => getWeeksForWindow(windowStart), [windowStart]);

	const isHaddock = isHaddockEnvironment();

	const weeksRangeLabel = useMemo(() => {
		if (!weeks.length) return "";
		return t("times.balance.weeksRange", {
			from: weeks[0].weekNumber,
			to: weeks[weeks.length - 1].weekNumber,
		});
	}, [weeks, t]);

	const isCurrentWindow = useMemo(
		() => windowStart.isSame(defaultWindowStart, "day"),
		[windowStart, defaultWindowStart]
	);

	const goToToday = useCallback(() => {
		setWindowStart(defaultWindowStart.clone());
	}, [defaultWindowStart]);

	const goBack = useCallback(() => {
		setWindowStart((prev) => prev.clone().subtract(NUM_WEEKS, "weeks"));
	}, []);

	const goForward = useCallback(() => {
		setWindowStart((prev) => prev.clone().add(NUM_WEEKS, "weeks"));
	}, []);

	const closeModal = useCallback(() => {
		dispatch(hideModal());
	}, [dispatch]);

	const fetchData = useCallback(async () => {
		if (!employeeId) return;
		setLoading(true);

		try {
			if (!objetivoAccountIdRef.current) {
				const acResp = await accountsService.getAll(OBJECTIVE_ACCOUNT_QUERY);
				if (acResp?.data?.length > 0) {
					objetivoAccountIdRef.current = acResp.data[0].id;
				}
			}
			if (!fichadasAccountIdRef.current) {
				const acResp = await accountsService.getAll(CLOCKED_HOURS_ACCOUNT_QUERY);
				if (acResp?.data?.length > 0) {
					fichadasAccountIdRef.current = acResp.data[0].id;
				}
			}

			const rangeStart = weeks[0].start.format("YYYY-MM-DD");
			const rangeEnd = weeks[NUM_WEEKS - 1].end.format("YYYY-MM-DD");
			const historyStart = "2000-01-01";
			const historyEnd = weeks[0].start
				.clone()
				.subtract(1, "day")
				.format("YYYY-MM-DD");

			const fichadasAccountId = fichadasAccountIdRef.current;
			const objetivoAccountId = objetivoAccountIdRef.current;

			const weekRanges = weeks.map((week) => ({
				start: week.start.format("YYYY-MM-DD"),
				end: week.end.format("YYYY-MM-DD"),
			}));

			const [
				hoursByWeek,
				historyHours,
				objetivoResp,
				manualResp,
				pendingResp,
				histObjetivoResp,
				histManualResp,
			] = await Promise.all([
				Promise.all(
					weekRanges.map((week) =>
						fetchClockedHours(employeeId, week.start, week.end)
					)
				),
				fetchClockedHours(employeeId, historyStart, historyEnd),
				objetivoAccountId
					? transactionsService.getAll(
							employeeId,
							objetivoAccountId,
							rangeStart,
							rangeEnd,
							"",
							0,
							"+day",
							1000
					  )
					: Promise.resolve({ data: [] }),
				fichadasAccountId
					? transactionsService.getAll(
							employeeId,
							fichadasAccountId,
							rangeStart,
							rangeEnd,
							"manual",
							0,
							"+day",
							1000
					  )
					: Promise.resolve({ data: [] }),
				timesService.getAll({
					employee_id: employeeId,
					start_from: rangeStart,
					start_to: rangeEnd,
					pending_only: true,
					page_size: 1000,
					page: 0,
				}),
				objetivoAccountId
					? transactionsService.getAll(
							employeeId,
							objetivoAccountId,
							historyStart,
							historyEnd,
							"",
							0,
							"+day",
							5000
					  )
					: Promise.resolve({ data: [] }),
				fichadasAccountId
					? transactionsService.getAll(
							employeeId,
							fichadasAccountId,
							historyStart,
							historyEnd,
							"manual",
							0,
							"+day",
							5000
					  )
					: Promise.resolve({ data: [] }),
			]);

			const objetivoTxs = objetivoResp?.data || [];
			const manualTxs = manualResp?.data || [];
			const pendingTimes = pendingResp?.data || [];
			const histObjetivoTxs = histObjetivoResp?.data || [];
			const histManualTxs = histManualResp?.data || [];

			const weekWithHours = hoursByWeek.findIndex((hours) => hours > 0);
			let firstClockInDay = null;
			if (historyHours > 0) {
				firstClockInDay = await findFirstDayWithHours(
					historyStart,
					historyEnd,
					(start, end) => fetchClockedHours(employeeId, start, end)
				);
			} else if (weekWithHours >= 0) {
				const week = weekRanges[weekWithHours];
				firstClockInDay = await findFirstDayWithHours(
					week.start,
					week.end,
					(start, end) => fetchClockedHours(employeeId, start, end)
				);
			}

			const { reglaTxs, histReglaTxs } = clockedHoursAsReglaTransactions({
				weeks: weekRanges,
				hoursByWeek,
				historyEnd,
				historyHours,
			});

			const balanceStats = buildWeeklyBalanceStats({
				weeks: weekRanges,
				reglaTxs,
				objetivoTxs,
				manualTxs,
				histReglaTxs,
				histObjetivoTxs,
				histManualTxs,
				firstClockInDay,
			});

			const data = weeks.map((week, index) => {
				const weekStats = balanceStats.weeks[index];
				const weekPending = pendingTimes.filter((time) => {
					const tStart = moment.utc(time.start).local();
					return (
						tStart.isSameOrAfter(week.start, "minute") &&
						tStart.isSameOrBefore(week.end, "minute")
					);
				});

				return {
					...weekStats,
					manualEntries: mapManualEntries(weekStats.weekManualTxs || []),
					pendingCount: weekPending.length,
				};
			});

			setWeekData(data);
			if (!userPickedAccumulatedViewRef.current) {
				setAccumulatedView(
					defaultAccumulatedView(Boolean(balanceStats.firstClockInDay))
				);
			}
		} catch (err) {
			console.error("Error cargando balance de fichajes", err);
		} finally {
			setLoading(false);
		}
	}, [employeeId, weeks]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleEmployeeChange = useCallback((id, employee) => {
		userPickedAccumulatedViewRef.current = false;
		setEmployeeId(id ? String(id) : null);
		if (employee) setKnownEmployee(employee);
	}, []);

	const openManualEntryModal = useCallback(
		(weekIndex, transaction = null) => {
			const week = weeks[weekIndex];
			if (!week || !fichadasAccountIdRef.current) return;

			const weekStats = weekData[weekIndex] || {};

			dispatch(
				showModal({
					modalProps: {
						open: true,
						title: transaction
							? t("times.balance.manualEntry.editTitle")
							: t("times.balance.manualEntry.newTitle"),
						style: { width: "460px" },
						content: (
							<ManualEntryModal
								transaction={transaction}
								employeeId={employeeId}
								accountId={fichadasAccountIdRef.current}
								week={week}
								weekStart={week.start}
								weekEnd={week.end}
								balanceMinutes={weekStats.balanceAfterManualMinutes ?? 0}
								cumulativeMinutes={pickCumulativeAfterManual(
									weekStats,
									accumulatedView
								)}
								hideWeeklyBalance={isHaddock}
								onSaved={fetchData}
								handleClose={closeModal}
							/>
						),
						closeModal,
					},
					modalType: "edit",
				})
			);
		},
		[
			weeks,
			weekData,
			employeeId,
			fetchData,
			closeModal,
			dispatch,
			isHaddock,
			accumulatedView,
			t,
		]
	);

	const navigateToApprovePending = useCallback(
		(weekIndex) => {
			const week = weeks[weekIndex];
			if (!week) return;

			localStorage.setItem("attendance_employeeIds", String(employeeId));
			localStorage.setItem(
				"attendance_startDate",
				week.start.format("YYYY-MM-DD")
			);
			localStorage.setItem("attendance_endDate", week.end.format("YYYY-MM-DD"));
			localStorage.setItem("attendance_datePreset", "custom");
			sessionStorage.setItem("attendance_initial_showApproved", "1");
			history.push("/attendance-supervisor");
		},
		[weeks, employeeId, history]
	);

	const registeredHoursTooltip = (
		<Tooltip className="tooltip-balance">
			{t("times.balance.registeredHoursTooltip")}
		</Tooltip>
	);

	const hasManualEntries = useMemo(
		() => weekData.some((d) => (d.manualEntries?.length ?? 0) > 0),
		[weekData]
	);

	const tableRows = useMemo(() => {
		const base = [
			{ id: ROW_IDS.trabajadas, label: t("times.summary.registeredHours") },
			{ id: ROW_IDS.objetivo, label: t("times.balance.targetHours") },
			{ id: ROW_IDS.balance, label: t("times.balance.weekBalance") },
			...(accumulatedView !== ACCUMULATED_VIEW.FIRST_CLOCK_IN
				? [
						{
							id: ROW_IDS.acumuladoContract,
							label: t("times.balance.accumulatedByContract"),
						},
				  ]
				: []),
			...(accumulatedView !== ACCUMULATED_VIEW.CONTRACT
				? [
						{
							id: ROW_IDS.acumuladoFirstClockIn,
							label: t("times.balance.accumulatedFromFirstClockIn"),
						},
				  ]
				: []),
		];

		if (hasManualEntries) {
			base.push({ id: ROW_IDS.manual, label: t("times.balance.manualEntries") });
		}

		base.push(
			{ id: ROW_IDS.createManual, label: t("times.balance.createManualEntry") },
			...(isHaddock
				? []
				: [
						{
							id: ROW_IDS.balanceAfterManual,
							label: t("times.balance.balanceAfterManual"),
						},
				  ]),
			{
				id: ROW_IDS.acumuladoAfterManual,
				label: t("times.balance.accumulatedAfterManual"),
			}
		);

		if (!weekData.length) return base;

		return base.map((row) => {
			const extended = { ...row };
			weekData.forEach((d, i) => {
				const key = `w${i}`;
				if (row.id === ROW_IDS.trabajadas) extended[key] = d.workedMinutes;
				else if (row.id === ROW_IDS.objetivo) extended[key] = d.objetivoMinutes;
				else if (row.id === ROW_IDS.balance) extended[key] = d.balanceMinutes;
				else if (row.id === ROW_IDS.acumuladoContract)
					extended[key] = d.cumulativeMinutesContract;
				else if (row.id === ROW_IDS.acumuladoFirstClockIn)
					extended[key] = d.cumulativeMinutesFirstClockIn;
				else if (row.id === ROW_IDS.manual) extended[key] = d.manualEntries;
				else if (row.id === ROW_IDS.balanceAfterManual)
					extended[key] = d.balanceAfterManualMinutes;
				else if (row.id === ROW_IDS.acumuladoAfterManual)
					extended[key] = pickCumulativeAfterManual(d, accumulatedView);
			});
			return extended;
		});
	}, [weekData, hasManualEntries, isHaddock, accumulatedView, t]);

	const tableColumns = useMemo(
		() => [
			{
				key: "label",
				header: (
					<div
						className="tb-view-mode"
						onClick={(event) => event.stopPropagation()}
						onMouseDown={(event) => event.stopPropagation()}
					>
						<label className="tb-view-mode__label" htmlFor="tb-accumulated-view">
							{t("times.balance.viewModeLabel")}
						</label>
						<select
							id="tb-accumulated-view"
							className="form-control tb-view-mode__select"
							value={accumulatedView}
							onChange={handleAccumulatedViewChange}
							aria-label={t("times.balance.viewModeLabel")}
						>
							<option value={ACCUMULATED_VIEW.CONTRACT}>
								{t("times.balance.viewModeContract")}
							</option>
							<option value={ACCUMULATED_VIEW.FIRST_CLOCK_IN}>
								{t("times.balance.viewModeFirstClockIn")}
							</option>
							<option value={ACCUMULATED_VIEW.BOTH}>
								{t("times.balance.viewModeBoth")}
							</option>
						</select>
					</div>
				),
				sortable: false,
				cellClassName: "tb-row-label",
				headerClassName: "tb-label-header",
				render: (row) => {
					if (row.id === ROW_IDS.trabajadas) {
						return (
							<span className="tb-row-label-with-info">
								{row.label}
								<OverlayTrigger
									placement="top"
									overlay={registeredHoursTooltip}
								>
									<button
										type="button"
										className="btn-transparent tb-info-btn"
										aria-label={t("times.balance.registeredHoursTooltip")}
									>
										<FiInfo />
									</button>
								</OverlayTrigger>
							</span>
						);
					}
					return row.label;
				},
			},
			...weeks.map((week, i) => ({
				key: `w${i}`,
				header: <WeekHeader week={week} />,
				sortable: false,
				align: "center",
				headerClassName: "tb-week-col-header",
				cellClassName: (row) => {
					if (row.id === ROW_IDS.manual) {
						const entries = row[`w${i}`] || [];
						const classes = ["tb-manual-cell", "tb-manual-entries-cell"];
						if (entries.length === 1) {
							classes.push("tb-manual-entries-cell--single");
						}
						return classes.join(" ");
					}
					if (row.id === ROW_IDS.createManual) {
						return "tb-manual-cell tb-create-manual-cell";
					}
					return undefined;
				},
				render: (row) => {
					if (row.id === ROW_IDS.createManual) {
						return (
							<div className="tb-manual-cell-inner tb-manual-cell-inner--single">
								<ManualEntryCreateCell
									week={week}
									employeeId={employeeId}
									accountId={fichadasAccountIdRef.current}
									isEditing={editingCreateWeekIndex === i}
									onStartEdit={() => setEditingCreateWeekIndex(i)}
									onFinishEdit={() => setEditingCreateWeekIndex(null)}
									onSaved={fetchData}
								/>
							</div>
						);
					}

					if (row.id === ROW_IDS.manual) {
						const entries = row[`w${i}`] || [];
						const groups = groupManualEntriesByDay(entries);
						const isCompactLayout = entries.length === 1;
						const { day, entries: dayEntries } = groups[0] || {};

						if (isCompactLayout && day) {
							return (
								<div className="tb-manual-cell-inner tb-manual-cell-inner--single">
									<div className="tb-manual-day-row tb-manual-day-row--single">
										{!isHaddock && (
											<span className="tb-manual-day-label">
												{moment(day).format("D MMM")}
											</span>
										)}
										<div className="tb-manual-pills">
											{dayEntries.map((entry) =>
												renderManualEntryPill(
													entry,
													i,
													openManualEntryModal,
													t
												)
											)}
										</div>
									</div>
								</div>
							);
						}

						return (
							<div
								className={`tb-manual-cell-inner${
									isHaddock ? "" : " tb-manual-cell-inner--aligned"
								}`}
							>
								{groups.map(({ day: groupDay, entries: groupEntries }) => (
									<div key={groupDay} className="tb-manual-day-row">
										{!isHaddock && (
											<span className="tb-manual-day-label">
												{moment(groupDay).format("D MMM")}
											</span>
										)}
										<div className="tb-manual-pills">
											{groupEntries.map((entry) =>
												renderManualEntryPill(
													entry,
													i,
													openManualEntryModal,
													t
												)
											)}
										</div>
									</div>
								))}
							</div>
						);
					}

					if (row.id === ROW_IDS.trabajadas) {
						const minutes = row[`w${i}`] ?? 0;
						const pendingCount = weekData[i]?.pendingCount || 0;
						return (
							<div className="tb-worked-cell">
								<span>{minutesToDisplay(minutes)}</span>
								{pendingCount > 0 && (
									<button
										type="button"
										className="tb-pending-link"
										onClick={() => navigateToApprovePending(i)}
									>
										{t("times.balance.pendingApprovals", {
											count: pendingCount,
										})}
									</button>
								)}
							</div>
						);
					}

					const minutes = row[`w${i}`] ?? 0;
					const display = minutesToDisplay(minutes);
					if (BALANCE_ROW_IDS.has(row.id)) {
						return (
							<span className={balanceSpanClass(row.id)}>{display}</span>
						);
					}
					return display;
				},
			})),
		],
		[
			weeks,
			weekData,
			employeeId,
			editingCreateWeekIndex,
			fetchData,
			openManualEntryModal,
			navigateToApprovePending,
			registeredHoursTooltip,
			isHaddock,
			accumulatedView,
			handleAccumulatedViewChange,
			t,
		]
	);

	return (
		<Layout className="page-times" context="times">
			<div className="heading">
				<div className="heading-left">
					<h1 className="title">{t("times.title")}</h1>
				</div>
			</div>

			{canSupervise && <TimesNavigationTabs />}

			<div className="subheading-time-registrations">
				<div className="time-registrations-month-nav">
					<button
						type="button"
						className="btn btn-sm btn-outline-secondary time-registrations-month-nav__today"
						onClick={goToToday}
						disabled={isCurrentWindow}
					>
						{t("datePresets.today")}
					</button>
					<div className="time-registrations-month-nav__chevrons">
						<button
							type="button"
							className="btn-transparent time-registrations-month-nav__chevron"
							onClick={goBack}
							aria-label={t("times.balance.previousWeeks")}
						>
							<FiChevronLeft />
						</button>
						<button
							type="button"
							className="btn-transparent time-registrations-month-nav__chevron"
							onClick={goForward}
							aria-label={t("times.balance.nextWeeks")}
						>
							<FiChevronRight />
						</button>
					</div>
					<span
						className="time-registrations-month-nav__label"
						title={weeksRangeLabel}
					>
						{weeksRangeLabel}
					</span>
				</div>

				{canSupervise && (
					<div className="subheading-actions">
						<EmployeeSelectSearch
							value={employeeId}
							onChange={handleEmployeeChange}
							knownEmployee={knownEmployee}
							allowClear={false}
							className="form-select-user"
						/>
					</div>
				)}
			</div>

			{loading ? (
				<div className="times-balance-loading">
					<Loading />
				</div>
			) : (
				<>
					<DataTable
						columns={tableColumns}
						rows={tableRows}
						rowKey={(row) => row.id}
						showHeaderSortAffix={false}
						className="times-balance-table-outer"
						tableClassName="times-balance-table"
						rowClassName={(row) => {
							if (row.id === ROW_IDS.manual) return "tb-manual-row";
							if (row.id === ROW_IDS.createManual) return "tb-create-manual-row";
							return undefined;
						}}
					/>

					<div className="alert alert-info tb-balance-help">
						<FiInfo className="tb-balance-help__icon" aria-hidden="true" />
						<p className="tb-balance-help__text">
							<Trans
								i18nKey="times.balance.manualEntry.helpText"
								components={{
									add: <strong />,
									toggle: <strong />,
								}}
							/>
						</p>
					</div>
				</>
			)}

			<Modal hideModal={closeModal} />
		</Layout>
	);
};

export default TimesBalance;
